/*
 * Cart complementary products (theme: MAIN coi)
 * --------------------------------------------------------------------------
 * For every line item in the cart (side drawer AND cart page) this fetches the
 * product's complementary products via the Product Recommendations API and
 * injects the "Goes well with" slider underneath the item. Single-variant
 * products get a one-click quick-add (reusing the theme's own btyAddToCart
 * flow, so the drawer refreshes exactly like a normal add). Multi-variant
 * products link through to their product page.
 *
 * Nothing else in the theme needs to change: it watches the cart for re-renders
 * (the drawer rebuilds its contents on every cart update) and re-populates.
 *
 * Paste-in: upload as assets/cart-complementary.js and load it once (it is
 * referenced from snippets/side-cart.liquid, which renders site-wide).
 */
(function () {
	'use strict';

	var WRAP_SELECTOR = '.cart-complementary-wrap';
	var ROOT_SELECTOR = '.cart-cc-root';
	var STYLE_ID = 'cart-cc-styles';

	/* ---- styles (injected once, so it works no matter where you paste) ---- */
	var CSS = '' +
		/* the drawer line item is a flex row (image + info); allow a 3rd child to wrap to a new full-width line */
		'.side-cart .product-item{flex-wrap:wrap;}' +
		WRAP_SELECTOR + '{display:block;flex:0 0 100%;width:100%;box-sizing:border-box;}' +
		WRAP_SELECTOR + ':empty,' + WRAP_SELECTOR + '.cart-cc-empty{display:none;}' +
		'tr.cart-complementary-row > td{padding:0 0 6px;border:0;}' +
		WRAP_SELECTOR + ' ' + ROOT_SELECTOR + '{margin-top:8px;padding-top:8px;border-top:1px solid rgba(0,0,0,.08);}' +
		WRAP_SELECTOR + ' .cart-cc-head{display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:6px;}' +
		WRAP_SELECTOR + ' .complementary-heading{font-size:11px;line-height:1.2;letter-spacing:.02em;opacity:.7;margin:0;}' +
		WRAP_SELECTOR + ' .complementary-control-slider{display:flex;align-items:center;gap:6px;}' +
		WRAP_SELECTOR + ' .complementary-control-slider .mivo-button{width:22px;height:22px;display:inline-flex;align-items:center;justify-content:center;padding:0;border:0;background:none;cursor:pointer;opacity:.8;}' +
		WRAP_SELECTOR + ' .complementary-control-slider .mivo-button[disabled]{opacity:.25;cursor:default;}' +
		WRAP_SELECTOR + ' .complementary-control-slider .mivo-button svg{width:9px;height:13px;}' +
		WRAP_SELECTOR + ' .mivo-pagination{font-size:11px;opacity:.6;min-width:30px;text-align:center;}' +
		WRAP_SELECTOR + ' .mivo-slider-viewport{overflow:hidden;}' +
		WRAP_SELECTOR + ' .mivo-slider-container{display:flex;}' +
		WRAP_SELECTOR + ' .mivo-slide{flex:0 0 100%;min-width:100%;}' +
		WRAP_SELECTOR + ' .cart-cc-card{display:flex;align-items:center;gap:10px;}' +
		WRAP_SELECTOR + ' .cart-cc-media{flex:0 0 40px;width:40px;height:40px;}' +
		WRAP_SELECTOR + ' .cart-cc-image{display:block;width:40px;height:40px;}' +
		WRAP_SELECTOR + ' .cart-cc-media img,' + WRAP_SELECTOR + ' .cart-cc-media svg{width:40px;height:40px;border-radius:8px;object-fit:cover;display:block;}' +
		WRAP_SELECTOR + ' .cart-cc-body{display:flex;align-items:center;gap:8px;flex:1 1 auto;min-width:0;}' +
		WRAP_SELECTOR + ' .cart-cc-info{flex:1 1 auto;min-width:0;}' +
		WRAP_SELECTOR + ' .cart-cc-title{font-size:12px;line-height:1.25;margin:0 0 1px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}' +
		WRAP_SELECTOR + ' .cart-cc-title a{text-decoration:none;}' +
		WRAP_SELECTOR + ' .cart-cc-price .price,' + WRAP_SELECTOR + ' .cart-cc-price .price-item{font-size:12px;}' +
		WRAP_SELECTOR + ' .cart-cc-card .cart-cc-form{margin:0;display:flex;align-items:center;flex:0 0 auto;}' +
		WRAP_SELECTOR + ' .cart-cc-card .product-action{flex:0 0 auto;width:34px;height:34px;display:inline-flex;align-items:center;justify-content:center;padding:0;border-radius:50%;}' +
		WRAP_SELECTOR + ' .cart-cc-card .product-action svg{width:17px;height:17px;}' +
		WRAP_SELECTOR + ' .cart-cc-card .product-action.loading{opacity:.6;pointer-events:none;}' +
		WRAP_SELECTOR + ' .cart-cc-card .product-warning{display:none;}' +
		/* tighter on small screens */
		'@media (max-width:600px){' +
		WRAP_SELECTOR + ' .cart-cc-media{flex:0 0 38px;width:38px;height:38px;}' +
		WRAP_SELECTOR + ' .cart-cc-media img{width:38px;height:38px;}' +
		WRAP_SELECTOR + ' .cart-cc-card .product-action{width:30px;height:30px;}' +
		'}';

	function injectStyles() {
		if (document.getElementById(STYLE_ID)) return;
		var s = document.createElement('style');
		s.id = STYLE_ID;
		s.textContent = CSS;
		(document.head || document.documentElement).appendChild(s);
	}

	function nudgeSliders() {
		// mivo-slider recalculates widths on window resize
		try { window.dispatchEvent(new Event('resize')); } catch (e) {}
	}

	function populate() {
		var wraps = document.querySelectorAll(WRAP_SELECTOR + ':not([data-cc-loaded])');
		if (!wraps.length) return;

		wraps.forEach(function (wrap) {
			var url = wrap.getAttribute('data-cc-url');
			wrap.setAttribute('data-cc-loaded', '1'); // claim it so we never double-fetch

			if (!url) { wrap.classList.add('cart-cc-empty'); return; }

			fetch(url, { credentials: 'same-origin' })
				.then(function (r) { return r.ok ? r.text() : ''; })
				.then(function (html) {
					if (!html) { wrap.classList.add('cart-cc-empty'); return; }

					var doc = new DOMParser().parseFromString(html, 'text/html');
					var root = doc.querySelector(ROOT_SELECTOR);
					if (!root) { wrap.classList.add('cart-cc-empty'); return; }

					// mivo-slider scopes its arrow/pagination selectors to itself,
					// so identical class selectors stay isolated per cart line.
					wrap.innerHTML = root.outerHTML;
					wrap.classList.remove('cart-cc-empty');
					wrap.classList.add('cart-cc-has');

					// wire quick-add buttons through the theme's own add-to-cart flow
					if (typeof window.btyAddToCart === 'function') {
						try { window.btyAddToCart(wrap); } catch (e) {}
					}

					window.requestAnimationFrame(nudgeSliders);
				})
				.catch(function () { wrap.classList.add('cart-cc-empty'); });
		});
	}

	var debounce;
	function schedule() {
		clearTimeout(debounce);
		debounce = setTimeout(populate, 200);
	}

	function init() {
		injectStyles();
		populate();

		// The cart drawer rebuilds its contents on every cart change, which wipes
		// our injected sliders. Watch the document and re-populate fresh slots.
		var bodyObserver = new MutationObserver(function (mutations) {
			for (var i = 0; i < mutations.length; i++) {
				var target = mutations[i].target;
				// ignore mutations we caused inside our own wraps
				if (target && target.closest && target.closest(WRAP_SELECTOR)) continue;
				schedule();
				return;
			}
		});
		bodyObserver.observe(document.body, { childList: true, subtree: true });

		// When the drawer opens, make sure sliders measure themselves correctly
		var htmlEl = document.documentElement;
		new MutationObserver(function () {
			if (htmlEl.classList.contains('side-cart-open')) {
				schedule();
				window.requestAnimationFrame(nudgeSliders);
			}
		}).observe(htmlEl, { attributes: true, attributeFilter: ['class'] });
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}
})();
